<template>
  <div class="d-flex align-center" data-locator="pagination-actions">
    <div v-if="!hidePageSize" class="d-flex align-center mr-6">
      <span class="mr-4">
        <slot name="label" >
          <span class="text-text-tertiary">{{ $t('common.pagination.linesPerPage') }}</span>
        </slot>
      </span>
      <AppSelect :model-value="pageSize"
                 class="page-size-select"
                 :items="pageSizeOptions"
                 no-label
                 variant="underlined"
                 data-locator="max-items-select"
                 @update:model-value="pageSizeChanged"
      />
    </div>

    <v-pagination :model-value="currentPage"
                  :length="computedLength"
                  :size="$vuetify.display.mobile ? 32 : 28"
                  :total-visible="7"
                  density="comfortable"
                  variant="text"
                  prev-icon="keyboard_arrow_left"
                  next-icon="keyboard_arrow_right"
                  :class="{ 'v-pagination--mobile': $vuetify.display.mobile }"
                  @update:model-value="currentPageChanged"
    />
  </div>
</template>

<script>
import AppSelect from '@/common/AppSelect.vue'

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 250]

/**
 * Default Pagination
 * @displayName AppPagination
 */
/**
 * A wrapper around the pagination component with default styling and custom default behavior
 * All Vuetify props for the pagination component are usable.
 * @see https://vuetifyjs.com/en/api/v-pagination/#props
 */
export default {
  name: 'AppPagination',
  components: { AppSelect },
  inheritAttrs: false,
  emits: [
    'update:current-page',
    'update:page-size',
  ],
  props: {
    currentPage: {
      type: Number,
      required: true,
    },
    /**
     * Items per page
     */
    pageSize: {
      type: Number,
      required: true,
    },
    /**
     * Total items count
     */
    total: {
      type: Number,
      required: true,
    },
    /**
     * Items per page options
     * @default [50, 100, 250]
     */
    pageSizes: {
      type: Array,
      default: () => DEFAULT_PAGE_SIZE_OPTIONS,
    },
    /**
     * Hide the page size selector
     */
    hidePageSize: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    computedLength () {
      return this.total !== 0
          ? Math.ceil(this.total / this.pageSize)
          : 0
    },
    pageSizeOptions () {
      return this.pageSizes.map(size => ({ title: size, value: size }))
    },
  },
  methods: {
    currentPageChanged (page) {
      this.$emit('update:current-page', page)
    },
    pageSizeChanged (pageSize) {
      this.$emit('update:page-size', pageSize)
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/typography-tokens";

:deep(.v-pagination--mobile) {
  .v-btn {
    font-size: 16px !important;
  }

  // On mobile only the current page is shown, framed by prev/next
  .v-pagination__item:not(.v-pagination__item--is-active) {
    display: none;
  }
}

:deep(*) {
  .page-size-select.v-input {
    .v-input__control {
      margin-top: -12px;
    }

    .v-field {
      align-items: center;

      &__field {
        font-size: 12px;
      }

      &__append-inner {
        font-size: 10px;
      }

      &__input {
        .v-select__selection {
          margin: 0;
          color: rgb(var(--v-theme-text-secondary));
        }
      }
    }
  }

  .v-pagination {
    &__prev,
    &__next,
    &__item {
      margin: 0 2px;

      .v-btn {
        color: rgb(var(--v-theme-text-tertiary));
        @include text ('body-md-bold');
        opacity: 1;

        &__overlay {
          background-color: transparent;
        }

        // Disable Vuetify ripple so it doesn't paint over the pressed state
        .v-ripple__container {
          display: none;
        }

        &:hover {
          background-color: rgba(var(--v-theme-surface-hover));
        }

        &:active {
          background-color: rgba(var(--v-theme-surface-pressed));
        }

        // Only the Figma focus ring — suppress Vuetify's own focus overlay
        &:focus-visible {
          box-shadow: 0 0 0 3px rgb(var(--v-theme-sky-400)); // focus-ring

          > .v-btn__overlay {
            opacity: 0;
          }
        }

        &--disabled {
          color: rgb(var(--v-theme-text-disabled));
          background-color: transparent;
        }

        // Ellipsis is disabled but must keep the regular text color
        &[ellipsis] {
          color: rgb(var(--v-theme-text-tertiary));
        }

        &:after {
          display: none;
        }
      }
    }

    &__item--is-active .v-btn {
      background-color: rgb(var(--v-theme-surface-brand));
      color: rgb(var(--v-theme-text-tertiary));
    }

    &__prev,
    &__next {
      .v-btn {
        color: rgb(var(--v-theme-icon-primary));

        .v-icon {
          font-size: 12px;
        }

        &--disabled {
          color: rgb(var(--v-theme-text-disabled));
          background-color: transparent;
        }
      }
    }
  }
}
</style>
